import axios from "axios";

// GCS resumable uploads require every chunk except the last to be a multiple of
// 256 KiB. 16 MiB satisfies that and keeps per-request memory bounded.
const CHUNK_SIZE = 16 * 1024 * 1024;
const MAX_CHUNK_RETRIES = 5;

// Must mirror UPLOAD_PROXY_PREFIX in pf/document/upload_sessions.py.
const UPLOAD_PROXY_PREFIX = "/__gcs-upload/";

// put_path comes from an API response; pin it to the same-origin nginx upload
// proxy so a tampered response can never redirect file bytes (and cookies) to
// another host.
const resolveUploadPath = (putPath: string): string => {
  const url = new URL(putPath, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith(UPLOAD_PROXY_PREFIX)) {
    throw new Error("invalid_upload_path");
  }
  return url.pathname + url.search;
};

interface UploadOptions {
  chunkSize?: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

// 308 "Resume Incomplete" carries the committed range in `Range: bytes=0-<last>`.
const nextOffsetFromRange = (rangeHeader: unknown, fallback: number): number => {
  if (typeof rangeHeader !== "string") return fallback;
  const match = rangeHeader.match(/bytes=\d+-(\d+)/);
  return match ? Number(match[1]) + 1 : fallback;
};

const isComplete = (status: number) => status === 200 || status === 201;

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(signal.reason ?? new Error("aborted"));
      },
      { once: true },
    );
  });

// Ask GCS how much it has committed so an interrupted upload can resume from the
// right offset instead of restarting. Returns the total size once complete.
const queryCommittedOffset = async (uploadPath: string, total: number, signal?: AbortSignal): Promise<number> => {
  const res = await axios.put(uploadPath, null, {
    headers: { "Content-Range": `bytes */${total}` },
    signal,
    validateStatus: status => isComplete(status) || status === 308,
  });
  if (isComplete(res.status)) return total;
  return nextOffsetFromRange(res.headers["range"], 0);
};

// Streams `file` to a same-origin proxy path in chunks; nginx forwards each chunk
// on to the GCS resumable session. Resumes on transient failure by re-querying the
// committed range. `onProgress` reports a 0..1 fraction of bytes committed.
export const uploadFileResumable = async (
  file: File,
  putPath: string,
  { chunkSize = CHUNK_SIZE, onProgress, signal }: UploadOptions = {},
): Promise<void> => {
  const uploadPath = resolveUploadPath(putPath);
  const total = file.size;
  let offset = 0;

  while (offset < total) {
    const end = Math.min(offset + chunkSize, total);
    const chunk = file.slice(offset, end);
    let attempt = 0;

    for (;;) {
      try {
        const res = await axios.put(uploadPath, chunk, {
          headers: {
            "Content-Range": `bytes ${offset}-${end - 1}/${total}`,
            "Content-Type": "application/octet-stream",
          },
          signal,
          validateStatus: status => isComplete(status) || status === 308,
        });

        if (isComplete(res.status)) {
          onProgress?.(1);
          return;
        }
        offset = nextOffsetFromRange(res.headers["range"], end);
        onProgress?.(offset / total);
        break;
      } catch (error) {
        if (signal?.aborted) throw error;
        attempt += 1;
        if (attempt > MAX_CHUNK_RETRIES) throw error;
        await delay(2 ** attempt * 250, signal);
        const committed = await queryCommittedOffset(uploadPath, total, signal);
        if (committed >= total) {
          onProgress?.(1);
          return;
        }
        offset = committed;
      }
    }
  }

  onProgress?.(1);
};
